import React, { useState, useEffect, useRef } from 'react';
import { useLeadsApi } from './useLeadsApi';
import PricingSnapshotFields, {
  emptyPricingSnapshot,
  normalizePricingSnapshot,
} from './PricingSnapshotFields';
import s from './Leads.module.scss';

const formatDateTime = (dt) => {
  if (!dt) return 'N/A';
  return new Date(dt).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const LinkBookingModal = ({ lead, onLink, onClose }) => {
  const { searchBookings, getRecentBookings, getRecommendedBookings } = useLeadsApi();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [activeTab, setActiveTab] = useState('recommended');
  const [recommended, setRecommended] = useState([]);
  const [loadingRecommended, setLoadingRecommended] = useState(false);
  const [recent, setRecent] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(false);
  const recentLoadedRef = useRef(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [pricing, setPricing] = useState(() => emptyPricingSnapshot(lead));
  const timerRef = useRef(null);

  const leadPhone = lead.customer_phone || lead.phone_number || '';

  useEffect(() => () => clearTimeout(timerRef.current), []);

  useEffect(() => {
    let cancelled = false;
    setLoadingRecommended(true);
    (async () => {
      try {
        const bookings = await getRecommendedBookings(lead.id);
        if (!cancelled) setRecommended(bookings);
      } catch {
        if (!cancelled) setRecommended([]);
      } finally {
        if (!cancelled) setLoadingRecommended(false);
      }
    })();
    return () => { cancelled = true; };
  }, [lead.id, getRecommendedBookings]);

  useEffect(() => {
    if (activeTab !== 'recent' || recentLoadedRef.current) return;
    if (!leadPhone) {
      recentLoadedRef.current = true;
      setLoadingRecent(false);
      return;
    }
    recentLoadedRef.current = true;
    let cancelled = false;
    setLoadingRecent(true);
    (async () => {
      try {
        const bookings = await getRecentBookings(leadPhone);
        if (!cancelled) setRecent(bookings);
      } catch {
        if (!cancelled) setRecent([]);
      } finally {
        if (!cancelled) setLoadingRecent(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, leadPhone, getRecentBookings]);

  useEffect(() => {
    clearTimeout(timerRef.current);
    const q = query.trim();
    if (q.length < 1) {
      setResults([]);
      setSearched(false);
      setSearching(false);
      return;
    }
    setSearching(true);
    timerRef.current = setTimeout(async () => {
      try {
        const bookings = await searchBookings(q);
        setResults(bookings);
        setSearched(true);
      } catch (e) {
        setResults([]);
        setError('Failed to search bookings');
      } finally {
        setSearching(false);
      }
    }, 400);
  }, [query, searchBookings]);

  const handleConfirm = async () => {
    if (!selectedBooking) return;
    setSaving(true);
    setError('');
    try {
      await onLink(lead, selectedBooking, normalizePricingSnapshot(pricing));
    } catch (e) {
      setError(e.message || 'Failed to link booking');
      setSaving(false);
    }
  };

  const nameDisplay = lead.customer_name || lead.business_name || 'Unknown';
  const phoneDisplay = lead.customer_phone || lead.phone_number || '';

  const pickupParts = [lead.pickup_locality, lead.pickup_city, lead.pickup_state].filter(Boolean);
  const pickupAddr = pickupParts.length ? pickupParts.join(', ') : null;
  const dropAddr = Array.isArray(lead.drop_addresses) && lead.drop_addresses.length
    ? lead.drop_addresses.join(' → ')
    : null;

  const leadDetails = [
    { icon: 'fa-tag', label: 'Lead', value: `#${lead.id}` },
    { icon: 'fa-crosshairs', label: 'Type', value: lead.lead_type || 'N/A' },
    { icon: 'fa-clock-o', label: 'Pickup time', value: lead.pickup_time ? formatDateTime(lead.pickup_time) : null },
    { icon: 'fa-truck', label: 'Pickup', value: pickupAddr },
    { icon: 'fa-map-marker', label: 'Drop', value: dropAddr },
    { icon: 'fa-car', label: 'Vehicle class', value: lead.vehicle_class_id != null ? `#${lead.vehicle_class_id}` : null },
    { icon: 'fa-calendar-o', label: 'Contact by', value: lead.contact_by ? formatDateTime(lead.contact_by) : null },
    { icon: 'fa-sticky-note-o', label: 'Remarks', value: lead.remarks || null },
  ].filter((d) => d.value != null && String(d.value).trim() !== '');

  const normalizeDigits = (v) => String(v ?? '').replace(/\D/g, '').replace(/^0+/, '').slice(-10);

  const isSameCustomer = (booking) => {
    const lcId = lead.customer_id;
    const bcId = booking.customer_id;
    if (lcId && bcId) return lcId === bcId;
    const lp = normalizeDigits(lead.customer_phone || lead.phone_number);
    const bp = normalizeDigits(booking.customer_phone);
    if (lp && bp) return lp === bp;
    return true;
  };

  const recentForLead = recent.filter(isSameCustomer);
  const recommendedForLead = recommended.filter(isSameCustomer);
  const searchResultsForLead = results.filter(isSameCustomer);

  const matchPct = (b) => (b.match_score != null ? Math.max(0, Math.min(100, Math.round(b.match_score))) : null);

  const renderBookingItem = (b) => {
    const routeParts = [];
    if (b.pickup_address) routeParts.push(`Pickup: ${b.pickup_address}`);
    if (Array.isArray(b.drop_addresses) && b.drop_addresses.length) {
      routeParts.push(`Drop: ${b.drop_addresses.join(' → ')}`);
    }
    const route = routeParts.join(' · ');
    const pct = matchPct(b);
    return (
      <button
        key={b.id}
        type="button"
        className={`${s.bookingResultItem} ${selectedBooking?.id === b.id ? s.active : ''}`}
        onClick={() => setSelectedBooking(b)}
      >
        <div className={s.bookingResultMain}>
          <strong>#{b.id}</strong>
          <span>{b.customer_name || 'N/A'}</span>
          <span className={s.bookingState}>{b.state}</span>
        </div>
        {route && (
          <div className={s.bookingRoute}>
            <i className="fa fa-map-marker" /> {route}
          </div>
        )}
        {pct != null && (
          <div className={s.bookingMatch}>
            <div className={s.bookingMatchBar}>
              <div className={s.bookingMatchFill} style={{ width: `${pct}%` }} />
            </div>
            <span className={s.bookingMatchPct}>{pct}%</span>
            {Array.isArray(b.match_reasons) && b.match_reasons.length > 0 && (
              <span className={s.bookingMatchReasons}>{b.match_reasons.join(' · ')}</span>
            )}
          </div>
        )}
        <div className={s.bookingResultMeta}>
          {b.pickup_time && (
            <span><i className="fa fa-clock-o" /> {formatDateTime(b.pickup_time)}</span>
          )}
          {b.customer_phone && (
            <span><i className="fa fa-phone" /> {b.customer_phone}</span>
          )}
          {b.amount != null && (
            <span><i className="fa fa-inr" /> {Number(b.amount).toLocaleString('en-IN')}</span>
          )}
        </div>
      </button>
    );
  };

  return (
    <div className={s.modalBackdrop} onClick={onClose}>
      <div className={s.modalContent} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '560px' }}>
        <div className={s.modalHeader}>
          <i className="fa fa-link" />
          Link Lead to Booking
        </div>
        <div className={s.modalBody}>
          <div className={s.modalLeadSummary}>
            <span className={s.modalLeadName}>{nameDisplay}</span>
            <span className={s.modalLeadPhone}>{phoneDisplay}</span>
            <span className={s.modalLeadStatus}>{lead.status}</span>
          </div>
          {leadDetails.length > 0 && (
            <div className={s.leadDetailsCard}>
              {leadDetails.map((d) => (
                <div key={d.label} className={s.leadDetailItem}>
                  <i className={`fa ${d.icon} ${s.leadDetailIcon}`} />
                  <span className={s.leadDetailLabel}>{d.label}</span>
                  <span className={s.leadDetailValue}>{d.value}</span>
                </div>
              ))}
            </div>
          )}
          <p className={s.modalPrompt}>
            Pick a recommended booking below, or switch to recent bookings, or search by booking ID, customer name or phone.
            Once linked, the lead will be marked as <strong>Converted</strong> so it can be tracked.
          </p>
          {lead.converted_booking_id && (
            <div className={s.linkedBookingBox}>
              <i className="fa fa-check-circle" />
              Currently linked to Booking #{lead.converted_booking_id}
            </div>
          )}
          {!query.trim() && (
            <div style={{ marginTop: '12px' }}>
              <div className={s.linkTabs}>
                <button
                  type="button"
                  className={`${s.linkTab} ${activeTab === 'recommended' ? s.active : ''}`}
                  onClick={() => setActiveTab('recommended')}
                >
                  <i className="fa fa-magic" /> Recommended
                </button>
                <button
                  type="button"
                  className={`${s.linkTab} ${activeTab === 'recent' ? s.active : ''}`}
                  onClick={() => setActiveTab('recent')}
                >
                  <i className="fa fa-clock-o" /> Recent (5)
                </button>
              </div>
              {activeTab === 'recommended' && (
                loadingRecommended ? (
                  <div className={s.linkSearching}>
                    <i className="fa fa-spinner fa-spin" /> Finding the best match...
                  </div>
                ) : recommendedForLead.length > 0 ? (
                  <div className={s.suggestionsBox}>
                    <div className={s.suggestionsHeader}><i className="fa fa-magic" /> Best matching bookings for this lead:</div>
                    {recommendedForLead.map(renderBookingItem)}
                  </div>
                ) : (
                  <div className={s.emptyHint}>
                    No matching bookings found for this customer. Try the Recent or search tabs below.
                  </div>
                )
              )}
              {activeTab === 'recent' && (
                loadingRecent ? (
                  <div className={s.linkSearching}>
                    <i className="fa fa-spinner fa-spin" /> Loading recent bookings...
                  </div>
                ) : recentForLead.length > 0 ? (
                  <div className={s.suggestionsBox}>
                    <div className={s.suggestionsHeader}><i className="fa fa-clock-o" /> Top 5 recent bookings for this customer:</div>
                    {recentForLead.slice(0, 5).map(renderBookingItem)}
                  </div>
                ) : (
                  <div className={s.emptyHint}>
                    No recent bookings found for this customer. Use the search below.
                  </div>
                )
              )}
            </div>
          )}
          <div style={{ marginTop: '12px' }}>
            <label className={s.formLabel}>Search booking</label>
            <input
              className={s.formInput}
              type="text"
              placeholder="Booking ID, customer name or phone..."
              value={query}
              onChange={(e) => { setQuery(e.target.value); setSelectedBooking(null); }}
              autoFocus
            />
          </div>
          {searching && (
            <div className={s.linkSearching}>
              <i className="fa fa-spinner fa-spin" /> Searching bookings...
            </div>
          )}
          {!searching && searchResultsForLead.length > 0 && (
            <div className={s.suggestionsBox} style={{ marginTop: '8px' }}>
              <div className={s.suggestionsHeader}><i className="fa fa-truck" /> Select a booking:</div>
              {searchResultsForLead.map(renderBookingItem)}
            </div>
          )}
          {!searching && searched && searchResultsForLead.length === 0 && query.trim() && (
            <div className={s.emptyHint} style={{ marginTop: '8px' }}>
              {results.length > 0
                ? 'No bookings belonging to this customer matched your search.'
                : `No bookings found for "${query.trim()}".`}
            </div>
          )}
          {selectedBooking && (
            <div className={s.linkedBookingBox} style={{ borderColor: '#bfdbfe', background: '#eff6ff' }}>
              <i className="fa fa-check" />
              Selected: Booking #{selectedBooking.id} - {selectedBooking.customer_name || ''} ({selectedBooking.state})
            </div>
          )}
          <PricingSnapshotFields value={pricing} onChange={setPricing} compact />
          {error && (
            <div className={s.emptyHint} style={{ color: '#ef4444', marginTop: '8px' }}>
              <i className="fa fa-exclamation-circle" /> {error}
            </div>
          )}
        </div>
        <div className={s.modalActions}>
          <button type="button" className={s.modalCancelBtn} onClick={onClose}>Cancel</button>
          <button
            type="button"
            className={s.modalConfirmBtn}
            onClick={handleConfirm}
            disabled={!selectedBooking || saving}
          >
            {saving ? <i className="fa fa-spinner fa-spin" /> : <i className="fa fa-link" />} Link Booking
          </button>
        </div>
      </div>
    </div>
  );
};

export default LinkBookingModal;
